import { useContactTypeList } from "@/api/services/admin/contact-type";
import { CardContainer } from "@/components/commons/CardContainer";
import { Button } from "@/components/elements/Button";
import { CountTable } from "@/components/elements/CountTable";
import { ContactTypeTable } from "@/features/contact-type/components/ContactTypeTable";
import { useContactTypeListSessionStorage } from "@/hooks/useContactTypeListSessionStorage";
import { useIsPageParamValid, useSanitizedPageParam } from "@/hooks/useSanitizedPageParam";
import { AppRoutes } from "@/routes/config";
import { Stack } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { FC, useEffect } from "react";
import { useLocation } from "react-router-dom";

type TContactTypePageProps = {};

export const ContactTypePage: FC<TContactTypePageProps> = () => {
  useSanitizedPageParam();

  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const { saveState, getState } = useContactTypeListSessionStorage();
  const isValidPage = useIsPageParamValid();
  const { data: contactTypeListResult, isLoading, isFetching } = useContactTypeList();

  // Restore saved page state on mount
  useEffect(() => {
    const savedState = getState();
    if (!savedState?.searchParams) return;
    if (search) return;

    navigate(`${pathname}?${savedState.searchParams}`, { replace: true });
  }, [pathname, search, navigate, getState]);

  useEffect(() => {
    if (!isValidPage) return;

    saveState({
      searchParams: search.startsWith("?") ? search.slice(1) : search,
    });
  }, [search, saveState, isValidPage]);

  const showLoading = isLoading || (isFetching && !contactTypeListResult);

  return (
    <CardContainer sx={{ borderRadius: "0 0 8px 8px" }}>
      <Stack gap={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <CountTable
            total={contactTypeListResult?.pagination.total}
            page={contactTypeListResult?.pagination.page}
            perPage={contactTypeListResult?.pagination.perPage}
          />
          <Button component={Link} size="sm" variant="outline" to={AppRoutes.contactTypeCreate}>
            新規作成
          </Button>
        </Stack>

        {contactTypeListResult ? (
          <ContactTypeTable isLoading={showLoading} isFetching={isFetching} data={contactTypeListResult} />
        ) : (
          <ContactTypeTable
            isLoading={showLoading}
            isFetching={isFetching}
            data={{
              data: [],
              pagination: { page: 1, perPage: 10, total: 0, totalPage: 0, nextPage: null, prevPage: null, count: 0 },
            }}
          />
        )}
      </Stack>
    </CardContainer>
  );
};
